import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Checkbox, Collapse, FormControlLabel, Grid2, IconButton } from "@mui/material";
import React from "react";

import type { ModuleConfig, ModuleGroup, SelectedPermission } from "./moduleGroupConfigs";
import { enforceDependencies } from "./moduleGroupConfigs";

const PURPLE = "#674BF5";

export type CheckStatus = "unchecked" | "checked" | "indeterminate";

/** Feature as returned in `module.children` from `GET /v1/subscription/features`. */
export interface Feature {
  id: number;
  code: string;
  name?: string;
  active?: boolean;
}

interface ModuleFeatureSelectorProps {
  config: ModuleConfig;
  /** `availableModules.find(m => m.code === config.code)?.children` — already filtered to the user's permissions. */
  moduleFeatures: Feature[];
  selectedPermissions: SelectedPermission[];
  setSelectedPermissions: React.Dispatch<React.SetStateAction<SelectedPermission[]>>;
  hiddenFeatureCodes?: string[];
}

const checkboxSx = {
  "&.Mui-checked": { color: PURPLE },
  "&.MuiCheckbox-indeterminate": { color: PURPLE },
};

// Words that should stay upper-cased when a token is humanized.
const ACRONYMS = new Set(["crm", "pg", "pnl", "id"]);

/**
 * Turn a feature into a readable checkbox label. The API `name` column is inconsistent between
 * modules (e.g. `CRM_CREATE_LEAD` vs `CREATE_LEAD`), so we derive from the action part of the
 * `code` (`CRM_MODULE.CREATE_LEAD` -> "Create Lead") and title-case it.
 */
export const humanizeFeatureLabel = (feature: { code: string; name?: string }): string => {
  const token = feature.code.includes(".")
    ? feature.code.slice(feature.code.indexOf(".") + 1)
    : feature.name ?? feature.code;
  const words = token
    .split(/[._]/)
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()));
  return words.join(" ") || feature.name || feature.code;
};

/** checked / indeterminate / unchecked for a set of codes; hidden codes count as active. */
export const computeCheckStatus = (
  codes: string[],
  selectedPermissions: SelectedPermission[],
  hiddenFeatureCodes: string[] = [],
): CheckStatus => {
  if (!codes.length) return "unchecked";
  const activeCount = codes.reduce((count, code) => {
    const isActive =
      hiddenFeatureCodes.includes(code) || selectedPermissions.some((p) => p.code === code && p.active);
    return isActive ? count + 1 : count;
  }, 0);
  if (activeCount === 0) return "unchecked";
  if (activeCount === codes.length) return "checked";
  return "indeterminate";
};

interface VisibleGroup extends ModuleGroup {
  availableCodes: string[];
}

/**
 * Renders one module's "feature selection box with module heading" from a single
 * `ModuleConfig`. Replaces the 20 hand-written `*ModuleAndFeatures` components.
 */
export const ModuleFeatureSelector: React.FC<ModuleFeatureSelectorProps> = ({
  config,
  moduleFeatures,
  selectedPermissions,
  setSelectedPermissions,
  hiddenFeatureCodes = [],
}) => {
  const availableCodes = React.useMemo(() => moduleFeatures.map((f) => f.code), [moduleFeatures]);

  const visibleGroups = React.useMemo<VisibleGroup[]>(
    () =>
      (config.groups ?? [])
        .map((group) => ({
          ...group,
          availableCodes: group.permissionCodes.filter((code) => availableCodes.includes(code)),
        }))
        .filter((group) => group.availableCodes.length > 0),
    [config.groups, availableCodes],
  );

  const isPermissionChecked = (code: string) => selectedPermissions.some((p) => p.code === code && p.active);

  const getGroupCheckStatus = (codes: string[]) => computeCheckStatus(codes, selectedPermissions, hiddenFeatureCodes);

  // Module header: "checked" once every available code that CAN be active is active. A code
  // whose mutually-exclusive peer is active is discounted (it can never be toggled on).
  const moduleCheckStatus = React.useMemo<CheckStatus>(() => {
    if (!availableCodes.length) return "unchecked";
    let activeCount = 0;
    let maxPossible = availableCodes.length;

    availableCodes.forEach((code) => {
      if (isPermissionChecked(code)) {
        activeCount++;
        return;
      }
      const group = visibleGroups.find((g) => g.permissionCodes.includes(code));
      const peerActive = group?.mutuallyExclusiveWith?.some((peer) => isPermissionChecked(peer));
      if (peerActive) maxPossible--;
    });

    if (activeCount === 0) return "unchecked";
    if (activeCount >= maxPossible) return "checked";
    return "indeterminate";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCodes, selectedPermissions, visibleGroups]);

  const applyMutualExclusivity = (state: SelectedPermission[], toggledCodes: string[]): SelectedPermission[] => {
    let next = state;
    toggledCodes.forEach((code) => {
      if (!next.some((p) => p.code === code && p.active)) return;
      const group = visibleGroups.find((g) => g.permissionCodes.includes(code));
      group?.mutuallyExclusiveWith?.forEach((exclusiveCode) => {
        next = next.map((p) => (p.code === exclusiveCode && p.active ? { ...p, active: false } : p));
      });
    });
    return next;
  };

  const commit = (mutate: (prev: SelectedPermission[]) => SelectedPermission[]) => {
    setSelectedPermissions((prev) => enforceDependencies(mutate(prev), config.groups ?? [], hiddenFeatureCodes));
  };

  const togglePermission = (code: string, checked: boolean) => {
    const feature = moduleFeatures.find((f) => f.code === code);
    if (!feature) return;
    commit((prev) => {
      const filtered = prev.filter((p) => p.code !== code);
      const next: SelectedPermission[] = [...filtered, { id: feature.id, code: feature.code, active: checked }];
      return checked ? applyMutualExclusivity(next, [code]) : next;
    });
  };

  const toggleGroupPermission = (codes: string[], checked: boolean) => {
    const entries = moduleFeatures
      .filter((f) => codes.includes(f.code))
      .map((f) => ({ id: f.id, code: f.code, active: checked }));
    commit((prev) => {
      const filtered = prev.filter((p) => !codes.includes(p.code));
      const next = [...filtered, ...entries];
      return checked ? applyMutualExclusivity(next, codes) : next;
    });
  };

  if (visibleGroups.length === 0) return null;

  return (
    <Grid2 key={`${config.code}-module-grid`} size={{ xs: 12, md: 6, lg: 4 }}>
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ================= MODULE HEADER ================= */}
        <Box sx={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0", px: 2, py: 1 }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                size="small"
                checked={moduleCheckStatus === "checked"}
                indeterminate={moduleCheckStatus === "indeterminate"}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => toggleGroupPermission(availableCodes, e.target.checked)}
                sx={checkboxSx}
              />
            }
            label={<Box sx={{ fontWeight: 700, fontSize: "1rem", color: "#333" }}>{config.name}</Box>}
          />
        </Box>

        {/* ================= GROUPS ================= */}
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {visibleGroups.map((group) => (
            <GroupRow
              key={group.label}
              group={group}
              moduleFeatures={moduleFeatures}
              groupStatus={getGroupCheckStatus(group.availableCodes)}
              disabled={
                !!group.dependsOn?.length && getGroupCheckStatus(group.dependsOn) === "unchecked"
              }
              isPermissionChecked={isPermissionChecked}
              onToggleGroup={(checked) => toggleGroupPermission(group.availableCodes, checked)}
              onTogglePermission={togglePermission}
            />
          ))}
        </Box>
      </Box>
    </Grid2>
  );
};

interface GroupRowProps {
  group: VisibleGroup;
  moduleFeatures: Feature[];
  groupStatus: CheckStatus;
  disabled: boolean;
  isPermissionChecked: (code: string) => boolean;
  onToggleGroup: (checked: boolean) => void;
  onTogglePermission: (code: string, checked: boolean) => void;
}

const GroupRow: React.FC<GroupRowProps> = ({
  group,
  moduleFeatures,
  groupStatus,
  disabled,
  isPermissionChecked,
  onToggleGroup,
  onTogglePermission,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const expandable = group.expandable !== false && group.availableCodes.length > 0;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Checkbox
              size="small"
              disabled={disabled}
              checked={groupStatus === "checked"}
              indeterminate={groupStatus === "indeterminate"}
              onChange={(e) => onToggleGroup(e.target.checked)}
              sx={checkboxSx}
            />
          }
          label={
            <Box>
              <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#444" }}>{group.label}</Box>
              {group.dependencyHint && (
                <Box sx={{ fontSize: "0.75rem", color: "#888" }}>{group.dependencyHint}</Box>
              )}
            </Box>
          }
        />

        {expandable && (
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${group.label}`}
            aria-expanded={expanded}
            sx={{ transition: "transform 0.25s ease", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {expandable && (
        <Collapse in={expanded} timeout={250} unmountOnExit>
          <Box sx={{ pl: 4, pt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {group.availableCodes.map((code) => {
              const feature = moduleFeatures.find((f) => f.code === code);
              if (!feature) return null;
              return (
                <FormControlLabel
                  key={code}
                  sx={{ width: "100%", margin: 0, "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                  control={
                    <Checkbox
                      size="small"
                      disabled={disabled}
                      checked={isPermissionChecked(code)}
                      onChange={(e) => onTogglePermission(code, e.target.checked)}
                      sx={checkboxSx}
                    />
                  }
                  label={humanizeFeatureLabel(feature)}
                />
              );
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
